import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import styles from "./Style.module.css";

/** Horizontal gap between date tabs (must match `.dateCarouselTrack` gap in CSS). */
const GAP_PX = 10;
const MAX_VISIBLE_DATES = 7;
const SWIPE_THRESHOLD_PX = 40;

/**
 * Minimum logical slot width used for visibleCount = floor((container + gap) / (slot + gap)).
 * Matches `.dateTab` footprint (padding + day circle) across breakpoints in Style.module.css.
 */
function minTabWidthForViewport(viewportWidth) {
  if (viewportWidth <= 400) return 56;
  if (viewportWidth <= 1440) return 62;
  return 72;
}

function computeVisibleCount(viewportWidth, datesLength) {
  if (!viewportWidth || !datesLength) return 1;
  const minTab = minTabWidthForViewport(viewportWidth);
  return Math.max(
    1,
    Math.min(MAX_VISIBLE_DATES, datesLength, Math.floor((viewportWidth + GAP_PX) / (minTab + GAP_PX)))
  );
}

export function DateTabsCarousel({
  dates,
  slotAvailabilityByDate = {},
  selectedDate,
  onDateSelect,
  onAutoDateSelect = onDateSelect,
  formatDate,
  canLoadNext = false,
  canLoadPrev = false,
  onNeedNextDates,
  onNeedPrevDates,
}) {
  const viewportRef = useRef(null);
  const touchStartRef = useRef(null);
  const suppressClickRef = useRef(false);
  const [startIndex, setStartIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(5);

  const pageSize = dates.length ? Math.min(visibleCount, dates.length) : 0;
  const maxStart = Math.max(0, dates.length - pageSize);

  const recalcVisibleCount = useCallback(() => {
    const el = viewportRef.current;
    if (!el || !dates.length) return;
    const n = computeVisibleCount(el.clientWidth, dates.length);
    setVisibleCount((prev) => (prev !== n ? n : prev));
  }, [dates.length]);

  useLayoutEffect(() => {
    recalcVisibleCount();
  }, [recalcVisibleCount]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => recalcVisibleCount());
    ro.observe(el);
    return () => ro.disconnect();
  }, [recalcVisibleCount]);

  useEffect(() => {
    setStartIndex((s) => Math.min(s, maxStart));
  }, [maxStart]);

  useEffect(() => {
    const idx = dates.indexOf(selectedDate);
    if (idx < 0) return;
    setStartIndex((si) => {
      if (idx < si) return Math.max(0, idx);
      if (idx >= si + pageSize) {
        return Math.min(maxStart, idx - pageSize + 1);
      }
      return si;
    });
  }, [selectedDate, dates, pageSize, maxStart]);

  const selectFirstAvailableDate = (nextStartIndex) => {
    const firstAvailableDate = dates
      .slice(nextStartIndex, nextStartIndex + pageSize)
      .find((date) => slotAvailabilityByDate[date] === true);
    if (firstAvailableDate && firstAvailableDate !== selectedDate) {
      onAutoDateSelect(firstAvailableDate);
    }
  };

  const goNext = () => {
    if (startIndex < maxStart) {
      const nextStartIndex = Math.min(startIndex + visibleCount, maxStart);
      setStartIndex(nextStartIndex);
      selectFirstAvailableDate(nextStartIndex);
      return;
    }
    onNeedNextDates?.();
  };

  const goPrev = () => {
    if (startIndex > 0) {
      const nextStartIndex = Math.max(0, startIndex - visibleCount);
      setStartIndex(nextStartIndex);
      selectFirstAvailableDate(nextStartIndex);
      return;
    }
    onNeedPrevDates?.();
  };

  const canPrev = startIndex > 0 || Boolean(canLoadPrev && onNeedPrevDates);
  const canNext = startIndex < maxStart || Boolean(canLoadNext && onNeedNextDates);
  const visibleDates = dates.slice(startIndex, startIndex + pageSize);  

  const handleTouchStart = (event) => {
    suppressClickRef.current = false;
    const touch = event.touches.length === 1 ? event.touches[0] : null;
    touchStartRef.current = touch
      ? { x: touch.clientX, y: touch.clientY }
      : null;
  };

  const handleTouchEnd = (event) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start || event.touches.length || !event.changedTouches.length) return;
    const touch = event.changedTouches[0];
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) <= Math.abs(dy)) return;

    // Browsing dates must not also select the date under the finger.
    suppressClickRef.current = true;
    if (dx < 0 && canNext) goNext();
    if (dx > 0 && canPrev) goPrev();
  };

  return (
    <div className={styles.dateCarouselRow} role="group" aria-label="Choose appointment date">
      <button
        type="button"
        className={`${styles.dateCarouselArrow} ${styles.dateCarouselArrowPrev}`}
        onClick={goPrev}
        disabled={!canPrev}
        aria-label="Previous dates"
        aria-disabled={!canPrev}
      />
      <div
        ref={viewportRef}
        className={styles.dateCarouselViewport}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={() => { touchStartRef.current = null; }}
        onClickCapture={(event) => {
          if (suppressClickRef.current && event.detail !== 0) {
            event.preventDefault();
            event.stopPropagation();
          }
          suppressClickRef.current = false;
        }}
      >
        <div className={styles.dateCarouselTrack}>
          {visibleDates.map((date, i) => {
            const globalIndex = startIndex + i;
            const { dayName, dayNum } = formatDate(date);
            const slotAvailable = slotAvailabilityByDate[date];
            return (
              <button
                key={`${date}-${globalIndex}`}
                className={`
                  ${styles.dateTab}
                  ${styles.dateCarouselItem}
                  ${selectedDate === date ? styles.active : ""}
                  ${slotAvailable === false ? styles.slotsNotAvailable : ""}
                  btn
                `}
                onClick={() => onDateSelect(date)}
                aria-pressed={selectedDate === date}
                type="button"
              >
                <div className={styles.dateTabDayNum}>{dayNum}</div>
                <div className={styles.dateTabDayName}>{dayName}</div>
              </button>
            );
          })}
        </div>
      </div>
      <button
        type="button"
        className={`${styles.dateCarouselArrow} ${styles.dateCarouselArrowNext}`}
        onClick={goNext}
        disabled={!canNext}
        aria-label="Next dates"
        aria-disabled={!canNext}
      />
    </div>
  );
}
